// Hibrit projelerde vizit eşleme.
//
// KURAL (yalnızca bu senaryoda başkasının takvimine vizit düşer):
//   Proje tasarımı HİBRİT ise ve vizit açan çalışan o projede PRİMER + BLINDED
//   ise, vizit aynı projeye bağlı UNBLINDED çalışan(lar)ın takvimine de düşer.
//
// NASIL
//   Takvimler ve tüm raporlar vizitleri `employeeId ==` ile okur; bu yüzden
//   unblinded çalışan için AYRI bir vizit belgesi ("eş vizit") oluşturulur. Eş
//   vizit kendi başına tamamlanır: masraf, stok ve değerlendirme kaynak vizitle
//   paylaşılmaz. Bilgi yalnızca blinded → unblinded yönünde akar; unblinded
//   çalışanın girdiği hiçbir şey blinded çalışanın vizitine yazılmaz.
//
// EŞLEME ANAHTARI: firma (Projects.customer) + proje + hasta ID. Hedef çalışanda
//   aynı anahtar ve tarihle vizit zaten varsa yenisi açılmaz.
//
// Eş vizit alanları: isMirror, mirrorOfVisitId, mirrorSourceEmployeeId,
//   mirrorSourceEmployeeName, customer. Kaynak vizit tarafında ek alan tutulmaz;
//   eşler `mirrorOfVisitId` sorgusuyla bulunur.

import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { isArchivedAssignment } from './assignmentHistory';
import { normalizePatientId } from './patientAccess';
import { isHybridProject } from './projectDesign';

const lower = (value) => String(value || '').trim().toLowerCase();

const projectEntries = (project) => {
  if (!project?.employees) return [];
  const entries = Array.isArray(project.employees) ? project.employees : Object.values(project.employees);
  return entries.filter((entry) => entry?.id && !isArchivedAssignment(entry));
};

export const isMirrorVisit = (visit) => Boolean(visit?.isMirror || visit?.mirrorOfVisitId);

/**
 * Vizit bu çalışan tarafından açıldığında kimlerin takvimine de düşmeli?
 *
 * Atamalar merkez bazlıdır (çalışan × merkez × rol). Çalışanın primer+blinded
 * olduğu merkezlerdeki unblinded çalışanlar hedeflenir; merkez bilgisi olmayan
 * eski atamalarda projedeki tüm unblinded çalışanlara düşülür.
 *
 * @returns {{id: string, name: string}[]} hibrit/primer/blinded değilse boş dizi
 */
export const findMirrorTargets = (project, employeeId) => {
  if (!isHybridProject(project) || !employeeId) return [];

  const entries = projectEntries(project);
  const ownRows = entries.filter(
    (entry) =>
      entry.id === employeeId &&
      lower(entry.workerType) === 'primer' &&
      lower(entry.blindStatus) === 'blinded'
  );
  if (ownRows.length === 0) return [];

  const ownCenters = new Set(ownRows.map((entry) => entry.centerId).filter(Boolean));
  const unblinded = entries.filter(
    (entry) => entry.id !== employeeId && lower(entry.blindStatus) === 'unblinded'
  );
  const sameCenter = unblinded.filter((entry) => ownCenters.has(entry.centerId));
  const chosen = ownCenters.size > 0 && sameCenter.length > 0 ? sameCenter : unblinded;

  const targets = new Map();
  chosen.forEach((entry) => {
    if (!targets.has(entry.id)) {
      targets.set(entry.id, { id: entry.id, name: `${entry.name || ''} ${entry.surname || ''}`.trim() });
    }
  });
  return [...targets.values()];
};

const visitDay = (visit) => visit.date || visit.firstVisitDate || '';

// Hedef çalışanda aynı proje + hasta + gün için vizit var mı?
const hasMatchingVisit = (existingVisits, visitData) =>
  existingVisits.some(
    (existing) =>
      normalizePatientId(existing.patientId) === normalizePatientId(visitData.patientId) &&
      visitDay(existing) === visitDay(visitData)
  );

/**
 * Oluşturulmuş kaynak vizitler için hedef çalışanlara eş vizit açar.
 *
 * @param {object}   project       Projects belgesi (customer, employees...)
 * @param {object}   source        { id, name } — vizit açan çalışan
 * @param {{id: string, data: object}[]} createdVisits
 * @param {{id: string, name: string}[]} targets  findMirrorTargets sonucu
 * @returns {Promise<{created: number, skipped: number, targets: object[]}>}
 */
export const createVisitMirrors = async ({ project, source, createdVisits, targets }) => {
  let created = 0;
  let skipped = 0;

  await Promise.all(
    targets.map(async (target) => {
      const snapshot = await getDocs(
        query(
          collection(db, 'Visits'),
          where('projectId', '==', project.id),
          where('employeeId', '==', target.id)
        )
      );
      const existing = snapshot.docs.map((item) => item.data());

      await Promise.all(
        createdVisits.map(async ({ id, data }) => {
          if (hasMatchingVisit(existing, data)) {
            skipped += 1;
            return;
          }
          await addDoc(collection(db, 'Visits'), {
            ...data,
            employeeId: target.id,
            // Screen fail kaydı kaynak vizitte tutulur; eş vizit proje screen-fail
            // listesinde ikinci satır oluşturmasın
            screenFail: false,
            customer: project.customer || '',
            isMirror: true,
            mirrorOfVisitId: id,
            mirrorSourceEmployeeId: source.id,
            mirrorSourceEmployeeName: source.name || '',
            createdAt: new Date(),
          });
          created += 1;
        })
      );
    })
  );

  return { created, skipped, targets };
};

const fetchMirrors = async (sourceVisitId) => {
  const snapshot = await getDocs(
    query(collection(db, 'Visits'), where('mirrorOfVisitId', '==', sourceVisitId))
  );
  return snapshot.docs;
};

// Kaynak vizitte değişince eş vizite de yansıyan planlama alanları
const SYNCED_FIELDS = ['date', 'firstVisitDate', 'startTime', 'patientId', 'visitNumber', 'visitType', 'hospital', 'city'];

/**
 * Kaynak vizit kaydedilince TAMAMLANMAMIŞ eş vizitlerin planlama alanlarını
 * günceller. Tamamlanmış eş vizite dokunulmaz.
 * @returns {Promise<number>} güncellenen eş vizit sayısı
 */
export const syncVisitMirrors = async (sourceVisitId, sourceData) => {
  const mirrors = await fetchMirrors(sourceVisitId);
  const open = mirrors.filter((mirror) => !mirror.data().status);

  const updates = open.map((mirror) => {
    const current = mirror.data();
    const changes = {};
    SYNCED_FIELDS.forEach((field) => {
      // Boş değerler (undefined / '') eşit sayılır: gereksiz yazma ve bildirim olmasın
      if (sourceData[field] !== undefined && (sourceData[field] ?? '') !== (current[field] ?? '')) {
        changes[field] = sourceData[field];
      }
    });
    return Object.keys(changes).length > 0
      ? updateDoc(mirror.ref, { ...changes, lastUpdated: new Date().toISOString() }).then(() => 1)
      : Promise.resolve(0);
  });

  return (await Promise.all(updates)).reduce((sum, count) => sum + count, 0);
};

/**
 * Kaynak vizit silinince tamamlanmamış eş vizitleri de siler. Tamamlanmış eş
 * vizit (unblinded çalışanın yaptığı iş) korunur; yalnızca bağı koparılır.
 * @returns {Promise<{deleted: number, kept: number}>}
 */
export const deleteVisitMirrors = async (sourceVisitId) => {
  const mirrors = await fetchMirrors(sourceVisitId);
  let deleted = 0;
  let kept = 0;

  await Promise.all(
    mirrors.map((mirror) => {
      if (mirror.data().status) {
        kept += 1;
        return updateDoc(mirror.ref, { mirrorSourceDeleted: true });
      }
      deleted += 1;
      return deleteDoc(mirror.ref);
    })
  );

  return { deleted, kept };
};
